import AsyncView from '@pixelunion/shopify-asyncview';
import morphdom from 'morphdom';
import rimg from '@pixelunion/rimg-shopify';
import { CountryProvinceSelector } from '@shopify/theme-addresses';

export default class StaticCart {
  constructor(section) {
    this.el = section.el;
    this.cartTable = this.el.querySelector('[data-cart-table]');
    this.cartDiscountContainer = this.el.querySelector('[data-cart-discount-container]');
    this.totalPrice = this.el.querySelector('[data-total-price]');
    this.quantityInputs = null;
    this.removeItem = null;
    this.updateTimeout = null;
    this.initializedClass = 'cart-initialized';
    this.cartInstructionsTextarea = document.querySelector('.cart-instructions textarea');

    if (this.cartInstructionsTextarea) {
      this.cartInstructionsTextarea.addEventListener('change', () => this.saveSpecialInstructions());
    }

    this._bindCartItemEvents();

    const { hasShippingCalculator } = section.data;

    if (hasShippingCalculator) {
      const countrySelect = document.getElementById('address_country');
      const provinceSelect = document.getElementById('address_province');
      const provinceContainer = document.getElementById('address_province_container');

      if (!countrySelect) return;

      const observer = new MutationObserver(() => {
        this.shippingSelects.forEach(select => {
          const { previousElementSibling, options, selectedIndex } = select;

          if (!previousElementSibling || !options || !options.length) return;

          previousElementSibling.innerText = options[selectedIndex >= 0 ? selectedIndex : 0].text;
        });
      });

      this.shippingSubmit = this.el.querySelector('[data-shipping-calculator-submit]');
      this.calculateShipping = () => this._calculateShipping();
      this.shippingSelects = this.el.querySelectorAll('.cart-shipping-calculator select');
      this.shippingSelects.forEach(shippingSelect => observer.observe(shippingSelect, { childList: true }));
      this.shippingCountryProvinceSelector = new CountryProvinceSelector(countrySelect.innerHTML);

      this.shippingCountryProvinceSelector
        .build(
          countrySelect,
          provinceSelect,
          {
            onCountryChange: provinces => {
              if (provinces.length) {
                provinceContainer.style.display = 'block';
              } else {
                provinceContainer.style.display = 'none';
              }

              // "Province", "State", "Region", etc. and "Postal Code", "ZIP Code", etc.
              // Even countries without provinces include a label.
              const { label, zip_label: zipLabel } = window.Countries[countrySelect.value];
              provinceContainer.querySelector('label[for="address_province"]').innerHTML = label;
              this.el.querySelector('label[for="address_zip"]').innerHTML = zipLabel;
            },
          },
        );

      this.shippingSubmit.addEventListener('click', this.calculateShipping);

      if (window.Theme.customerAddress) {
        this._calculateShipping(window.Theme.customerAddress);
      }
    }
  }

  _bindCartItemEvents() {
    this.quantityInputs = this.el.querySelectorAll('[data-quantity-key]');
    this.removeItem = this.el.querySelectorAll('[data-remove-item]');
    this.cartTable = this.el.querySelector('[data-cart-table]');
    this.cartDiscountContainer = this.el.querySelector('[data-cart-discount-container]');
    this.totalPrice = this.el.querySelector('[data-total-price]');

    this.quantityInputs.forEach(el => el.addEventListener('input', event => this._updateItemQuantity(event)));
    this.removeItem.forEach(el => el.addEventListener('click', event => this._updateItemQuantity(event)));
  }

  _unbindCartItemEvents() {
    this.quantityInputs.forEach(el => el.removeEventListener('input', event => this._updateItemQuantity(event)));
    this.removeItem.forEach(el => el.removeEventListener('click', event => this._updateItemQuantity(event)));

    this.quantityInputs = null;
    this.removeItem = null;
  }

  onSectionUnload() {
    this.shippingSubmit.removeEventListener('click', this.calculateShipping);

    if (this.cartInstructionsTextarea) {
      this.cartInstructionsTextarea.removeEventListener('change', () => this.saveSpecialInstructions());
    }
  }

  _updateItemQuantity(event) {
    event.preventDefault();

    let quantityInput;
    let quantity;
    let lineItemKey;

    if (event.currentTarget.hasAttribute('data-remove-item')) {
      quantity = 0;
      lineItemKey = event.currentTarget.dataset.removeItem;
    } else {
      quantityInput = event.currentTarget;
      quantity = quantityInput.value;

      if (quantity === '') {
        return;
      }

      lineItemKey = quantityInput.dataset.quantityKey;
    }

    // cancel any pending requests
    if (this.updateTimeout !== null) {
      clearTimeout(this.updateTimeout);
    }

    this.updateTimeout = setTimeout(() => {
      // Debounce to avoid firing requests while user is still entering number

      const thisTimeoutId = this.updateTimeout;
      const request = new XMLHttpRequest();

      request.open('POST', `${window.Theme.routes.cart_change_url}.js`, true);
      request.setRequestHeader('Content-Type', 'application/json');
      request.responseType = 'json';
      request.onload = () => {
        const cart = request.response;

        // If another request is in progress, discard this update
        if (this.updateTimeout && this.updateTimeout !== thisTimeoutId) {
          return;
        }
        if (cart.item_count === 0) {
          // reload to show empty cart state
          window.location.reload();
          return;
        }
        this.updateCart(cart, thisTimeoutId);
      };
      request.send(JSON.stringify({
        quantity,
        id: lineItemKey,
      }));
    }, 300);
  }

  updateCart(cart, timeoutId) {
    document.querySelector('.cart-count-number').innerText = cart.item_count;

    // Swap the cart contents to update discount labels.
    AsyncView.load(
      window.Theme.routes.cart_url,
      { view: 'ajax' },
    )
      .then(({ data, html }) => {
        // If another request is in progress, discard this update
        if (this.updateTimeout !== timeoutId) {
          return;
        }

        this._unbindCartItemEvents();

        // Inject new cart table contents
        const newTableContainer = document.createElement('div');
        newTableContainer.innerHTML = html.table;

        morphdom(
          this.cartTable,
          newTableContainer.querySelector('table'),
          {
            onBeforeElUpdated: (fromEl, toEl) => {
              // Skip images if src matches
              // - we don't want to reload lazy loaded images
              if (fromEl.tagName === 'IMG' && fromEl.src === toEl.src) {
                return false;
              }
              return true;
            },
          },
        );

        rimg.watch(this.cartTable);

        // Inject new cart level discounts
        const newDiscountContainer = document.createElement('div');
        newDiscountContainer.innerHTML = html.discounts;
        this.cartDiscountContainer.innerHTML = newDiscountContainer.querySelector('[data-cart-discount-container]').innerHTML;

        // Update total
        this.totalPrice.innerHTML = data.totalPrice;
        this._bindCartItemEvents();
      })
      .catch(() => window.location.reload());
  }

  saveSpecialInstructions() {
    const newNote = this.cartInstructionsTextarea.value;
    const request = new XMLHttpRequest();

    request.open('POST', `/cart/update.js?note=${encodeURI(newNote)}`);
    request.responseType = 'json';
    request.send();
  }

  _calculateShipping(customerAddress) {
    let shippingAddress = {};

    if (customerAddress) {
      shippingAddress = customerAddress;
    } else {
      const addressCountry = document.querySelector('#address_country');
      const addressProvince = document.querySelector('#address_province');
      const addressZip = document.querySelector('#address_zip');
      shippingAddress.country = addressCountry ? addressCountry.value : '';
      shippingAddress.province = addressProvince ? addressProvince.value : '';
      shippingAddress.zip = addressZip ? addressZip.value : '';
    }

    const queryString = Object.keys(shippingAddress)
      .map(key => `${encodeURIComponent(`shipping_address[${key}]`)}=${encodeURIComponent(shippingAddress[key])}`)
      .join('&');

    const request = new XMLHttpRequest();

    request.open('GET', `/cart/shipping_rates.json?${queryString}`);
    request.responseType = 'json';
    request.onload = () => {
      const { status, response } = request;

      if (status === 422) {
        this._handleErrors(response);
        return;
      }

      const rates = response.shipping_rates;

      let address = `${shippingAddress.zip}, ${shippingAddress.province}, ${shippingAddress.country}`;
      if (!shippingAddress.province.length) {
        address = `${shippingAddress.zip}, ${shippingAddress.country}`;
      }
      if (!shippingAddress.zip.length) {
        address = `${shippingAddress.province}, ${shippingAddress.country}`;
      }
      if (!shippingAddress.province.length || !shippingAddress.zip.length) {
        address = shippingAddress.country;
      }

      const cartShippingCalculatorResponse = this.el.querySelector('.cart-shipping-calculator-response');

      cartShippingCalculatorResponse.innerHTML = '<p class="shipping-calculator-response message"></p><ul class="shipping-rates"/></ul>';

      const ratesFeedback = this.el.querySelector('.shipping-calculator-response');

      if (rates.length > 1) {
        const firstRate = window.Theme.Currency.formatMoney(rates[0].price, window.Theme.moneyFormat);
        const multipleShippingRates = window.Theme.shippingCalcMultiRates
          .replace('--address--', address)
          .replace('--number_of_rates--', rates.length)
          .replace('--rate--', `<span class='money'>${firstRate}</span>`);
        ratesFeedback.innerHTML = multipleShippingRates;
      } else if (rates.length === 1) {
        const oneShippingRate = window.Theme.shippingCalcOneRate
          .replace('--address--', address);
        ratesFeedback.innerHTML = oneShippingRate;
      } else {
        ratesFeedback.innerHTML = window.Theme.shippingCalcNoRates;
      }

      const cartShippingRates = this.el.querySelector('.shipping-rates');

      for (let i = 0; i < rates.length; i++) {
        const rate = rates[i];
        const price = window.Theme.Currency.formatMoney(rate.price, window.Theme.moneyFormat);
        const rateValues = window.Theme.shippingCalcRateValues
          .replace('--rate_title--', rate.name)
          .replace('--rate--', `<span class='money'>${price}</span>`);
        const rateEl = document.createElement('li');

        rateEl.innerHTML = rateValues;

        cartShippingRates.appendChild(rateEl);
      }
    };
    request.send();
  }

  _handleErrors(errors) {
    let errorMessage = '';

    if ('zip' in errors) {
      errorMessage = window.Theme.shippingCalcErrorMessage.replace('--error_message--', errors.zip);
    } else {
      errorMessage = errors;
    }

    document.querySelector('.cart-shipping-calculator-response').innerHTML = `<p>${errorMessage}</p>`;
  }
}
